import React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Cell } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from "../../ui/chart";
import { CHART_MARGIN_TOP } from "../chartStyles";
import { formatNumber } from "../utils";
import { InsightLegendContent } from "./InsightLegendContent";
import { CategoryColorTooltipContent } from "./CategoryColorTooltipContent";
import { IncidentCountByIncidentType } from "../../../types/insightDashboard";
import { CardNotes } from "../CardNotes";

interface BreakdownBarChartProps {
  title: string;
  description?: string;
  notes?: string[];
  focusData: IncidentCountByIncidentType[];
  comparisonData: IncidentCountByIncidentType[];
  focusYear: number | string;
  comparisonYear: number | string;
  // What each period is called when that isn't its year, like "Past 6 Months"
  focusLabel?: string;
  comparisonLabel?: string;
  descriptionTerm?: string;
  getCategoryColor?: (key: string, muted?: boolean) => string;
}

interface ChartDataEntry {
  key: string;
  displayName: string;
  [year: string]: string | number;
}

export function BreakdownBarChart({
  title,
  description,
  notes,
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  focusLabel = String(focusYear),
  comparisonLabel = String(comparisonYear),
  descriptionTerm = "results",
  getCategoryColor,
}: BreakdownBarChartProps) {
  const chartData: ChartDataEntry[] = focusData.map((focusEntry) => {
    const comparisonEntry = comparisonData.find(
      (compEntry) => compEntry.displayName === focusEntry.displayName,
    );
    return {
      key: focusEntry.key,
      displayName: focusEntry.displayName,
      [focusYear]: focusEntry.value,
      [comparisonYear]: comparisonEntry ? comparisonEntry.value : 0,
    };
  });

  const chartConfig = {
    [focusYear]: {
      label: focusLabel,
      color: "var(--chart-1)",
    },
    [comparisonYear]: {
      label: comparisonLabel,
      color: "var(--chart-2)",
    },
  };

  const renderBar = (dataKey: string, defaultFill: string, muted: boolean = false) => {
    if (getCategoryColor) {
      return (
        <Bar dataKey={dataKey} radius={4}>
          {chartData.map((entry) => (
            <Cell key={`cell-${dataKey}-${entry.key}`} fill={getCategoryColor(entry.key, muted)} />
          ))}
        </Bar>
      );
    }
    return <Bar dataKey={dataKey} fill={defaultFill} radius={4} />;
  };

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description ||
            `Comparison of ${descriptionTerm} by type for ${focusLabel} and ${comparisonLabel}`}
        </CardDescription>
        <CardNotes notes={notes} />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="tw:aspect-auto tw:h-[250px] tw:w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: CHART_MARGIN_TOP,
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="displayName" tickLine={false} tickMargin={10} axisLine={false} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <ChartTooltip
              cursor={false}
              content={
                getCategoryColor ? (
                  <CategoryColorTooltipContent
                    getCategoryColor={getCategoryColor}
                    comparisonYear={comparisonYear}
                  />
                ) : (
                  <ChartTooltipContent indicator="dot" />
                )
              }
            />
            {renderBar(String(focusYear), `var(--color-${String(focusYear)})`, false)}
            {renderBar(String(comparisonYear), `var(--color-${String(comparisonYear)})`, true)}
            {!getCategoryColor && <ChartLegend content={<InsightLegendContent />} />}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
